



















































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Global } from '@/store';
import LineChart from '@/pages/schedules/components/curves/LineChart.js';

@Component({ components: { LineChart } })
class DaliDimmingInfo extends Vue {
  @Global.State('lang') lang;

  dialog = false;
  /*
    A DALI driver's light output against its dimming value follows a curve of this shape.
    The exponent only has to make the gap obvious - the dialog says in words that the
    graph is an illustration, and no value here reaches the curve the user is editing.
  */
  DALI_EXPONENT = 2.6;
  /*
    The dimming value both curves are marked at, and where their names are anchored. At
    75% each label has the room it needs on its clear side; nearer the right edge the
    blue one would be pushed back over its own curve.
  */
  MARKED_VALUE = 75;
  SAMPLE_STEP = 1;

  get dir() {
    return this.lang === 'en' ? 'ltr' : 'rtl';
  }

  daliIntensity(dimming_value) {
    return 100 * Math.pow(dimming_value / 100, this.DALI_EXPONENT);
  }

  get chart_data() {
    const dali_points = [];
    for (let value = 0; value <= 100; value += this.SAMPLE_STEP) {
      dali_points.push({ x: value, y: this.daliIntensity(value) });
    }

    const marked_value = this.MARKED_VALUE;
    const marked_index = dali_points.findIndex((point) => point.x === marked_value);

    return {
      datasets: [
        {
          data: [{ x: 0, y: 0 }, { x: marked_value, y: marked_value }, { x: 100, y: 100 }],
          borderColor: '#2E7D32',
          backgroundColor: 'rgba(46, 125, 50, 0.08)',
          borderWidth: 2.5,
          lineTension: 0,
          pointBackgroundColor: '#2E7D32',
          pointBorderColor: '#2E7D32',
          pointRadius: [0, 4, 0]
        },
        {
          data: dali_points,
          borderColor: '#1565C0',
          backgroundColor: 'rgba(21, 101, 192, 0.08)',
          borderWidth: 2.5,
          lineTension: 0.2,
          pointBackgroundColor: '#1565C0',
          pointBorderColor: '#1565C0',
          pointRadius: dali_points.map((unused, index) => (index === marked_index ? 4 : 0))
        }
      ]
    };
  }

  /*
    The names are drawn onto the canvas beside the point they belong to, rather than
    floated over it in HTML: only the chart knows where a curve actually runs, so a label
    placed by hand drifts away from its line as soon as the dialog changes width.
  */
  get chart_plugins() {
    /*
      Both names sit right against their own dot, on the side where their curve has
      already moved out of the way: both curves rise, so the space above and to the left
      of a point is clear of the green line, and the space below and to the right of a
      point is clear of the blue one. Placing a chip on the other side, or straight under
      a point, puts the line through the text - which is why neither needs a leader.
    */
    const labels = [
      {
        dataset: 0,
        index: 1,
        side: 'left',
        offset_y: -18,
        text: `${this.$t('Linear representation in the system')}`,
        color: '#2E7D32',
        background: 'rgba(46, 125, 50, 0.14)'
      },
      {
        dataset: 1,
        index: this.MARKED_VALUE / this.SAMPLE_STEP,
        side: 'right',
        offset_y: 14,
        text: `${this.$t('DALI dimming')}`,
        color: '#1565C0',
        background: 'rgba(21, 101, 192, 0.14)'
      }
    ];

    return [{
      afterDatasetsDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();
        ctx.font = 'bold 12px "Open Sans", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        /*
          The plot itself reads left to right in both languages, so the text does too:
          left to the bidi default the canvas inherits the page's RTL and swaps a name
          like "עמעום DALI" around.
        */
        ctx.direction = 'ltr';

        labels.forEach((label) => {
          const meta = chart.getDatasetMeta(label.dataset);
          const point = meta && meta.data[label.index];
          if (!point) return;

          const height = 20;
          const width = ctx.measureText(label.text).width + 16;
          const center_y = point._model.y + label.offset_y;
          const gap = 8;
          // Beside the dot on its clear side, and kept inside the plot so a long name
          // cannot run off the canvas.
          const anchored_x = label.side === 'left'
            ? point._model.x - gap - width / 2
            : point._model.x + gap + width / 2;
          const center_x = Math.min(
            Math.max(anchored_x, chart.chartArea.left + width / 2),
            chart.chartArea.right - width / 2
          );

          this.drawRoundedRect(ctx, center_x - width / 2, center_y - height / 2, width, height, label.background);
          ctx.fillStyle = label.color;
          ctx.fillText(label.text, center_x, center_y);
        });

        ctx.restore();
      }
    }];
  }

  drawRoundedRect(ctx, x, y, width, height, color) {
    const radius = height / 2;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  }

  get chart_options() {
    /* Percentages on both axes, at the five steps that make the shape readable. */
    const axis = (labelString) => ({
      ticks: {
        min: 0,
        max: 100,
        stepSize: 25,
        fontColor: '#6a6a6a',
        callback: (value) => `${value}%`
      },
      scaleLabel: { display: true, labelString, fontColor: '#6a6a6a' },
      gridLines: { color: '#e8e8ef' }
    });

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      legend: { display: false },
      // An illustration, not data to read values off.
      tooltips: { enabled: false },
      scales: {
        xAxes: [{ type: 'linear', position: 'bottom', ...axis(`${this.$t('Dimming value (%)')}`) }],
        yAxes: [axis(`${this.$t('Relative light intensity (%)')}`)]
      }
    };
  }
}

export default DaliDimmingInfo;
